/**
 * Admin Event Registrations Management Page
 * View and manage event/webinar registrations
 * Access: super_admin, ops_admin only
 */

import React, { useState, useEffect } from 'react';
import {
  Users,
  Trash2,
  Calendar,
  Mail,
  Phone,
  Building2,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Download,
  Search,
  UserPlus,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ExternalLink,
  Newspaper
} from 'lucide-react';
import {
  getUpcomingEvents,
  getEventRegistrations,
  deleteRegistration,
  UpcomingEvent,
  EventRegistration
} from '@/services/api/eventRegistrations';
import { listWebinars, Webinar } from '@/services/api/webinarManagement';
import { apiClient } from '@/services/apiClient';
import PhoneInput from '@/components/ui/PhoneInput';
import { PageHeader, PageContainer } from '@/components/weavein';

interface NewsletterSubscriber {
  id: number;
  full_name: string;
  email: string;
  organization_name: string;
  subscription_type: string;
  subscribed_at: string;
  source: string;
}

export default function EventRegistrationsPage() {
  const [activeTab, setActiveTab] = useState<'registrations' | 'newsletter'>('registrations');
  const [webinars, setWebinars] = useState<Webinar[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<EventRegistration | null>(null);
  const [converting, setConverting] = useState(false);
  const [opportunityForm, setOpportunityForm] = useState({
    organizationName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: ''
  });

  // Newsletter subscribers state
  const [newsletterSubscribers, setNewsletterSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [loadingNewsletter, setLoadingNewsletter] = useState(false);
  const [newsletterSearchTerm, setNewsletterSearchTerm] = useState('');

  useEffect(() => {
    loadWebinars();
  }, []);

  useEffect(() => {
    if (selectedEvent && activeTab === 'registrations') {
      loadRegistrations(selectedEvent);
    }
  }, [selectedEvent, activeTab]);

  useEffect(() => {
    if (activeTab === 'newsletter') {
      loadNewsletterSubscribers();
    }
  }, [activeTab]);

  const loadWebinars = async () => {
    try {
      setLoading(true);
      const response = await listWebinars();
      setWebinars(response.webinars);

      // Check if there's a pre-selected event from webinar management page
      const preSelectedEvent = localStorage.getItem('selected_webinar_event');
      if (preSelectedEvent && response.webinars.some(w => w.event_id === preSelectedEvent)) {
        setSelectedEvent(preSelectedEvent);
        // Clear the selection after use
        localStorage.removeItem('selected_webinar_event');
      } else if (response.webinars.length > 0 && !selectedEvent) {
        // Auto-select first webinar if available
        setSelectedEvent(response.webinars[0].event_id);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to load webinars:', err);
      setError(err.response?.data?.detail || 'Failed to load webinars');
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrations = async (eventId: string) => {
    try {
      setLoadingRegistrations(true);
      const response = await getEventRegistrations(eventId);
      setRegistrations(response.registrations);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load registrations:', err);
      setError(err.response?.data?.detail || 'Failed to load registrations');
      setRegistrations([]);
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const loadNewsletterSubscribers = async () => {
    try {
      setLoadingNewsletter(true);
      const response = await apiClient.get('/api/events/newsletter/subscribers');
      setNewsletterSubscribers(response.data.subscribers);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load newsletter subscribers:', err);
      setError(err.response?.data?.detail || 'Failed to load newsletter subscribers');
      setNewsletterSubscribers([]);
    } finally {
      setLoadingNewsletter(false);
    }
  };

  const handleDeleteRegistration = async (registration: EventRegistration) => {
    if (!confirm(`Are you sure you want to delete the registration for "${registration.full_name}"?`)) {
      return;
    }

    try {
      await deleteRegistration(registration.id);
      setSuccess(`Registration for ${registration.full_name} deleted successfully`);
      // Refresh the list
      if (selectedEvent) {
        loadRegistrations(selectedEvent);
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete registration:', err);
      setError(err.response?.data?.detail || 'Failed to delete registration');
    }
  };

  const handleConvertToOpportunity = async () => {
    if (!selectedRegistration) return;

    setConverting(true);
    try {
      // Call the new convert-to-opportunity endpoint
      const response = await apiClient.post('/api/events/registrations/convert-to-opportunity', {
        registration_id: selectedRegistration.id,
        organization_name: opportunityForm.organizationName,
        contact_name: opportunityForm.contactName,
        contact_email: opportunityForm.contactEmail,
        contact_phone: opportunityForm.contactPhone,
        event_id: selectedEvent
      });

      const result = response.data;
      const message = result.is_new_organization
        ? `Organization "${result.organization_name}" created as opportunity`
        : `Contact added to existing organization "${result.organization_name}"`;

      setSuccess(message);
      setShowConvertModal(false);
      setSelectedRegistration(null);
      setOpportunityForm({ organizationName: '', contactName: '', contactEmail: '', contactPhone: '' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to create opportunity:', err);
      setError(err.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setConverting(false);
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    const search = searchTerm.toLowerCase();
    return (
      reg.full_name.toLowerCase().includes(search) ||
      reg.email.toLowerCase().includes(search) ||
      reg.organization_name?.toLowerCase().includes(search) ||
      reg.phone_number?.includes(search)
    );
  });

  const filteredNewsletterSubscribers = newsletterSubscribers.filter(sub => {
    const search = newsletterSearchTerm.toLowerCase();
    return (
      sub.full_name.toLowerCase().includes(search) ||
      sub.email.toLowerCase().includes(search) ||
      sub.organization_name?.toLowerCase().includes(search) ||
      sub.source?.toLowerCase().includes(search)
    );
  });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Organization', 'Phone', 'Registered At'];
    const rows = filteredRegistrations.map(reg => [
      reg.full_name,
      reg.email,
      reg.organization_name || 'Self',
      reg.phone_number,
      new Date(reg.registered_at).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registrations-${selectedEvent}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportNewsletterToCSV = () => {
    const headers = ['Name', 'Email', 'Organization', 'Source', 'Subscribed At'];
    const rows = filteredNewsletterSubscribers.map(sub => [
      sub.full_name,
      sub.email,
      sub.organization_name || '',
      sub.source,
      sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const selectedWebinar = webinars.find(w => w.event_id === selectedEvent);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Content"
        title="Event Registrations & Newsletter"
        description="View and manage webinar registrations and newsletter subscribers"
        actions={
          <>
            <button
              onClick={() => {
                localStorage.setItem('activeTab', 'webinar-management');
                window.dispatchEvent(new Event('storage'));
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            {activeTab === 'registrations' ? (
              <>
                <button
                  onClick={() => selectedEvent && loadRegistrations(selectedEvent)}
                  disabled={loadingRegistrations}
                  className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingRegistrations ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={exportToCSV}
                  disabled={filteredRegistrations.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={loadNewsletterSubscribers}
                  disabled={loadingNewsletter}
                  className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingNewsletter ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={exportNewsletterToCSV}
                  disabled={filteredNewsletterSubscribers.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </>
            )}
          </>
        }
        tabs={[
          { key: 'registrations', label: 'Event Registrations' },
          {
            key: 'newsletter',
            label: 'Newsletter Subscribers',
            count: newsletterSubscribers.length > 0 ? newsletterSubscribers.length : undefined,
          },
        ]}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as 'registrations' | 'newsletter')}
      />
      <PageContainer>

      {/* Filters - Registrations Tab */}
      {activeTab === 'registrations' && (
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-ink-700">Event:</label>
              <div className="relative">
                <select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  className="appearance-none pl-4 pr-10 py-2 border border-v2border rounded-lg bg-surface text-ink text-sm min-w-[300px]"
                >
                  <option value="">Select an event...</option>
                  {webinars.map((webinar) => (
                    <option key={webinar.event_id} value={webinar.event_id}>
                      {webinar.title} ({webinar.event_date}) - {webinar.registration_count || 0} registrations
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              </div>
            </div>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder="Search by name, email, organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg bg-surface text-ink text-sm"
              />
            </div>

            <span className="text-sm text-ink-500">
              {filteredRegistrations.length} of {registrations.length} registrations
            </span>
          </div>
        </div>
      )}

      {/* Filters - Newsletter Tab */}
      {activeTab === 'newsletter' && (
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder="Search by name, email, organization, source..."
                value={newsletterSearchTerm}
                onChange={(e) => setNewsletterSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg bg-surface text-ink text-sm"
              />
            </div>

            <span className="text-sm text-ink-500">
              {filteredNewsletterSubscribers.length} of {newsletterSubscribers.length} subscribers
            </span>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </div>
      )}

      {success && (
        <div>
          <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 p-4 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        </div>
      )}

      {/* Selected Event Info - Registrations Tab */}
      {activeTab === 'registrations' && selectedWebinar && (
        <div>
          <div className="bg-[#5B4B8A]/10 dark:bg-[#5B4B8A]/20 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-ink">{selectedWebinar.title}</h3>
                <p className="text-sm text-ink-500 flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {selectedWebinar.event_date} at {selectedWebinar.event_time}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {selectedWebinar.registration_count || 0} registered
                  </span>
                </p>
              </div>
              {selectedWebinar.zoom_link && (
                <a
                  href={selectedWebinar.zoom_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#5CBDB9] hover:text-[#4A9E9B] flex items-center gap-1 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Zoom Link
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Registrations Table - Registrations Tab */}
      {activeTab === 'registrations' && (
      <div>
        <div className="bg-surface rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#5B4B8A]" />
            </div>
          ) : !selectedEvent ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <p className="text-ink-500">Select an event to view registrations</p>
            </div>
          ) : loadingRegistrations ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#5B4B8A]" />
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <p className="text-ink-500">
                {searchTerm ? 'No registrations match your search' : 'No registrations for this event yet'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-paper dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Registrant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {filteredRegistrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-paper-2/50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-ink">
                        {registration.full_name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm space-y-1">
                        <a
                          href={`mailto:${registration.email}`}
                          className="flex items-center gap-1.5 text-[#5CBDB9] hover:text-[#4A9E9B]"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          {registration.email}
                        </a>
                        <div className="flex items-center gap-1.5 text-ink-500">
                          <Phone className="w-3.5 h-3.5" />
                          {registration.phone_number}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-ink-700">
                        <Building2 className="w-4 h-4 text-ink-400" />
                        {registration.organization_name || 'Self'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-ink-500">
                      {new Date(registration.registered_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedRegistration(registration);
                            setOpportunityForm({
                              organizationName: registration.organization_name || `Individual - ${registration.full_name}`,
                              contactName: registration.full_name,
                              contactEmail: registration.email,
                              contactPhone: registration.phone_number || ''
                            });
                            setShowConvertModal(true);
                          }}
                          className="p-2 text-ink-400 hover:text-[#5B4B8A]"
                          title="Add as Organization Opportunity"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRegistration(registration)}
                          className="p-2 text-ink-400 hover:text-red-600"
                          title="Delete Registration"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {/* Newsletter Subscribers Table - Newsletter Tab */}
      {activeTab === 'newsletter' && (
        <div>
          <div className="bg-surface rounded-xl shadow overflow-hidden">
            {loadingNewsletter ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#5B4B8A]" />
              </div>
            ) : filteredNewsletterSubscribers.length === 0 ? (
              <div className="text-center py-12">
                <Newspaper className="w-12 h-12 text-ink-400 mx-auto mb-4" />
                <p className="text-ink-500">
                  {newsletterSearchTerm ? 'No subscribers match your search' : 'No newsletter subscribers yet'}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-paper dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                      Subscriber
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                      Subscribed
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-v2border-muted">
                  {filteredNewsletterSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-paper-2/50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-ink">
                          {subscriber.full_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={`mailto:${subscriber.email}`}
                          className="flex items-center gap-1.5 text-[#5CBDB9] hover:text-[#4A9E9B]"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          {subscriber.email}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-ink-700">
                          <Building2 className="w-4 h-4 text-ink-400" />
                          {subscriber.organization_name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          subscriber.subscription_type === 'newsletter'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {subscriber.source}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-ink-500">
                        {subscriber.subscribed_at ? new Date(subscriber.subscribed_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Convert to Opportunity Modal */}
      {showConvertModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-v2border">
              <h2 className="text-xl font-bold text-ink flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#5B4B8A]" />
                Add as Organization Opportunity
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Create or add to an organization as an "Opportunity" for sales follow-up.
                  If the organization already exists, a contact will be added to it.
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={opportunityForm.organizationName}
                    onChange={(e) => setOpportunityForm(prev => ({ ...prev, organizationName: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-[#5B4B8A] focus:border-transparent"
                    placeholder="Enter organization name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={opportunityForm.contactName}
                    onChange={(e) => setOpportunityForm(prev => ({ ...prev, contactName: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-[#5B4B8A] focus:border-transparent"
                    placeholder="Enter contact name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Contact Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={opportunityForm.contactEmail}
                    onChange={(e) => setOpportunityForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-[#5B4B8A] focus:border-transparent"
                    placeholder="Enter contact email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Contact Phone
                  </label>
                  <PhoneInput
                    value={opportunityForm.contactPhone}
                    onChange={(v) => setOpportunityForm(prev => ({ ...prev, contactPhone: v }))}
                    className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-[#5B4B8A] focus:border-transparent"
                    placeholder="Enter contact phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Status
                  </label>
                  <p className="px-4 py-2.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg text-yellow-800 dark:text-yellow-300 font-medium">
                    OPPORTUNITY
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-v2border">
              <button
                type="button"
                onClick={() => {
                  setShowConvertModal(false);
                  setSelectedRegistration(null);
                  setOpportunityForm({ organizationName: '', contactName: '', contactEmail: '', contactPhone: '' });
                }}
                className="px-4 py-2 text-ink-700 hover:bg-paper-2 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToOpportunity}
                disabled={converting || !opportunityForm.organizationName || !opportunityForm.contactName || !opportunityForm.contactEmail}
                className="px-6 py-2 bg-[#5CBDB9] text-white rounded-lg hover:bg-[#4A9E9B] transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {converting && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Opportunity
              </button>
            </div>
          </div>
        </div>
      )}
      </PageContainer>
    </div>
  );
}
